// =============================================================================
// components/discover/AvatarStack.tsx — overlapping member-avatar stack + count
// (reference IMG_5876/5873: "avatar stack + 15,867 Members").
//
// DATA HONESTY: GET /communities carries only memberCount — no member avatar
// URLs — and GET /communities/:name/members 403s for NON-members ("Must be a
// member to view member list"), which is exactly who sees discovery surfaces.
// So the circles are decorative lavender-gradient badges (person glyphs, seeded
// per community so they're stable), while the COUNT is always real data. If a
// caller does have real avatar URIs it can pass them via `uris`.
// =============================================================================
import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import CachedImage from '../ui/CachedImage';
import LinearGradient from 'react-native-linear-gradient';
import { Ionicons } from '../ui/Icon';
import { fonts } from '../../constants/theme';
import { DISCOVER_GRADIENTS, compactCount } from './tokens';

interface AvatarStackProps {
  /** Real member avatar URLs when available (falsy entries are skipped). */
  uris?: (string | null | undefined)[];
  /** Stable seed (community name/id) so decorative circles don't reshuffle. */
  seed: string;
  /** Real member count (rendered as "12.4K Members"). */
  count: number;
  /** Circle diameter. */
  size?: number;
  /** Count label — "Members" unless the caller truly has holder data. */
  label?: string;
  /** Text/number color — defaults for on-gradient (light) usage. */
  onDark?: boolean;
}

const MAX_CIRCLES = 3;

export default function AvatarStack({
  uris,
  seed,
  count,
  size = 22,
  label = 'Members',
  onDark = true,
}: AvatarStackProps) {
  const real = (uris || []).filter((u): u is string => typeof u === 'string' && u.trim().length > 0);
  const circles = Math.min(MAX_CIRCLES, Math.max(1, count > 0 ? Math.min(count, MAX_CIRCLES) : 1));

  // Seeded offset into the gradient table keeps decorative circles stable.
  let h = 0;
  const s = String(seed || '');
  for (let i = 0; i < s.length; i++) h = (h * 33 + s.charCodeAt(i)) >>> 0;

  const shownLabel = count === 1 && label === 'Members' ? 'Member' : label;
  const numberColor = onDark ? '#FFFFFF' : '#1A1A2E';
  const labelColor = onDark ? 'rgba(255,255,255,0.78)' : '#8E8E9E';
  const ringColor = onDark ? 'rgba(255,255,255,0.9)' : '#FFFFFF';

  return (
    <View style={styles.row} accessible accessibilityLabel={`${compactCount(count)} ${shownLabel.toLowerCase()}`}>
      <View style={[styles.stack, { height: size }]}>
        {Array.from({ length: circles }).map((_, i) => {
          const uri = real[i];
          const grad = DISCOVER_GRADIENTS[(h + i) % DISCOVER_GRADIENTS.length];
          const common = {
            width: size,
            height: size,
            borderRadius: size / 2,
            marginLeft: i === 0 ? 0 : -size * 0.38,
            borderWidth: 1.5,
            borderColor: ringColor,
          } as const;
          return uri ? (
            <CachedImage key={i} source={{ uri }} style={[styles.circle, common]} />
          ) : (
            <LinearGradient
              key={i}
              colors={[...grad]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={[styles.circle, styles.center, common]}
            >
              <Ionicons name="person" size={size * 0.5} color="rgba(255,255,255,0.92)" />
            </LinearGradient>
          );
        })}
      </View>
      <Text style={[styles.count, { color: numberColor }]}>{compactCount(count)}</Text>
      <Text style={[styles.label, { color: labelColor }]}>{shownLabel}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  stack: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  circle: {
    overflow: 'hidden',
  },
  center: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  count: {
    fontSize: 14,
    ...fonts.bold,
  },
  label: {
    fontSize: 12,
    ...fonts.medium,
  },
});
